<script setup lang="ts">
import { onBeforeMount, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'

import { currentIndex, debouncedOpenMarker, debouncedHandleSearch, handleSearchClick, openAccordion, searchLoading, searchQuery, searchResults } from '@/elements/maps'
import { accordions, addAccordions } from '@/components/accordion/accordion'
import { currentLocation, initMap } from '.'
import { courierData } from '@/elements/repository'
import { addMarkers } from '@/elements/markters'
import { showSidebar } from '@/components/controls/sidebar'

import Accordion from '../components/accordion/Accordion.vue'

const mapElement = ref< HTMLDivElement | null >( null )
const searchListElement = ref< HTMLLIElement[] | null >( null )

const eventMap = shallowRef< { [ key: string ]: () => void } >( {
	ArrowUp: () => {
		currentIndex.value--
	},
	ArrowDown: () => {
		if ( ! searchListElement.value ) return
		if ( currentIndex.value < searchListElement.value?.length - 1 ) {
			currentIndex.value++
		}
	},
	Enter: () => {
		if ( ! searchListElement.value ) return
		if ( currentIndex.value >= 0 && currentIndex.value < searchListElement.value?.length ) {
			const selectedItem = searchListElement.value?.[ currentIndex.value ]
			selectedItem?.click()
		}
	}
} )

function handleKeyDown( event: KeyboardEvent ) {
	if ( ! searchListElement.value ) return
	eventMap.value[ event.key ]?.()
	updateHighlight( searchListElement.value )
}

function updateHighlight( listItems: HTMLLIElement[] ) {
	listItems.forEach( ( item, index ) => {
		if ( index === currentIndex.value ) {
			item.classList.add( 'highlighted' )
			item.scrollIntoView( { block: 'nearest', behavior: 'smooth' } )
		} else {
			item.classList.remove( 'highlighted' )
		}
	} )
}

watch( courierData, addMarkers, { deep: true } )
watch( courierData, addAccordions, { deep: true } )

onMounted( async () => {
	initMap( mapElement )
	addMarkers()
	document.addEventListener( 'keydown', handleKeyDown )

	if ( currentLocation.value ) {
		debouncedOpenMarker( currentLocation.value?.id )
	}
} )

onBeforeMount( addAccordions )
onBeforeUnmount( document.removeEventListener( 'keydown', handleKeyDown ) )
</script>

<template>
	<div class="innosend">
		<div class="__sidebar" :class="{ open: showSidebar }">
			<div class="__container">
				<div class="__row">
					<div class="__searchbar">
						<input
							type="search"
							name="search-couriers"
							id="search"
							placeholder="Keizersgracht 1, 1015 CC Amsterdam"
							v-model="searchQuery"
							@input.prevent="debouncedHandleSearch"
							:class="{ loading: searchLoading }"
						/>
						<div class="__toggle" @click.prevent="showSidebar = ! showSidebar">☰</div>
					</div>
					<template v-if="searchResults?.length > 0">
						<div class="search-results-container">
							<ul class="search-results-list">
								<template v-for="(result, index) in searchResults" :key="index">
									<li ref="searchListElement" class="search-result-item" :title="result.raw.display_name" @click.prevent="handleSearchClick( result )">
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="24"
											height="24"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											stroke-width="2"
											stroke-linecap="round"
											stroke-linejoin="round"
											class="feather feather-map-pin search-result-icon"
										>
											<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
											<circle cx="12" cy="10" r="3"></circle>
										</svg>
										<strong class="search-result-text">{{ result.raw.display_name }}</strong>
									</li>
								</template>
							</ul>
						</div>
					</template>
					<template v-else>
						<Accordion :accordion="accordions" :callback="openAccordion"></Accordion>
					</template>
				</div>
			</div>
		</div>
		<div ref="mapElement" class="__map"></div>
	</div>
</template>
